import type { HttpClient } from '@wix/cli-http-client';
import { suggestDomains } from '@wix/ambassador-domains-search-v2-suggestion/http';
import { isLoggedInToAccount } from '@wix/cli-auth';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { createHttpClient } from '@wix/cli-http-client';
import { z } from 'zod';

const domainSuggestionSchema = z.object({
  domain: z.string(),
});

const suggestDomainsResponseSchema = z.object({
  suggestions: z.array(domainSuggestionSchema).default([]),
});

type DomainSuggestion = z.infer<typeof domainSuggestionSchema>;

interface SearchDomainsParams {
  limit?: number;
  query: string;
  tlds?: string[];
}

class DomainsClient {
  constructor(private readonly httpClient: HttpClient) {}

  search = async (params: SearchDomainsParams): Promise<DomainSuggestion[]> => {
    try {
      const { data } = await this.httpClient.request(suggestDomains(params));

      return suggestDomainsResponseSchema.parse(data).suggestions;
    } catch (error) {
      throw new CliError({
        cause: error,
        code: CliErrorCode.FailedToGetDomainSuggestions(),
      });
    }
  };
}

export async function getDomainsClient(): Promise<DomainsClient> {
  const authState = await isLoggedInToAccount();

  if (!authState) {
    throw new CliError({ cause: null, code: CliErrorCode.LoginRequired() });
  }

  const httpClient = createHttpClient({
    createHeaders: authState.getRequestHeaders,
    getAppToken: () => authState.getAccessToken(),
    type: 'api',
  });

  return new DomainsClient(httpClient);
}
